import {
  effectiveDepositEUR,
  type DiscountBookingFields,
} from "./rent-discount";

/**
 * First-month rent fraction from the move-in calendar date (YYYY-MM-DD, UTC calendar parts).
 *
 * Daily pro-rata: the tenant is charged only for the days actually occupied in
 * the move-in month — from the move-in day through the end of the month,
 * inclusive — as a share of that month's total days.
 *
 *   fraction = (daysInMonth − moveInDay + 1) / daysInMonth
 *
 * Move-in on the 1st therefore charges the full month (fraction = 1).
 */
export function getMoveInProRataFraction(
  moveInIso: string | null | undefined
): number {
  const raw = (moveInIso || "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return 1;
  const y = parseInt(raw.slice(0, 4), 10);
  const mo = parseInt(raw.slice(5, 7), 10);
  const day = parseInt(raw.slice(8, 10), 10);
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(mo) ||
    !Number.isFinite(day) ||
    mo < 1 ||
    mo > 12
  )
    return 1;

  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  if (day < 1 || day > daysInMonth) return 1;

  const remainingInclusive = daysInMonth - day + 1;
  return remainingInclusive / daysInMonth;
}

export function isoSameYearMonth(
  isoA: string | null | undefined,
  isoB: string | null | undefined
): boolean {
  const a = (isoA || "").trim().slice(0, 7);
  const b = (isoB || "").trim().slice(0, 7);
  if (!a || !b) return false;
  return a === b;
}

export function roundRentEUR(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Single authoritative monthly rent for a booking:
 * `bookings.monthly_rent` (admin / contract) wins; `rooms.price` is only used when monthly_rent is unset (e.g. legacy).
 */

export function getFullMonthlyRentFromBooking(
  booking: {
    monthly_rent?: number | null;
    rooms?: { price?: number | null } | null;
  } | null
): number {
  if (!booking) return 0;
  const r = booking.monthly_rent;
  if (r != null && Number(r) > 0) return Number(r);
  const p = booking.rooms?.price;
  if (p != null && Number(p) > 0) return Number(p);
  return 0;
}

/**
 * Monthly rent for list/summary UIs when rent `payments` rows exist: use the largest scheduled
 * amount across months (matches what is billed; admin edits on any month apply). If booking
 * `monthly_rent` is higher than all lines, those lines are still the operational source of truth.
 * When there are no rent payment rows, fall back to booking `monthly_rent` / room price.
 * `preferCalendarMonthYm` is optional for API compatibility; resolution does not early-return on it.
 *
 * Each row is read GROSS — `gross_amount_cents ?? amount_cents`, the same COALESCE the
 * owner-facing SQL uses. `amount_cents` is what the TENANT is charged after a CoLiviq-funded
 * credit (a referral reward, or a waived free month billed at EUR 0); the pre-discount figure
 * lives in `gross_amount_cents`. Reading the net figure would drop a discounted row out of the
 * max, so after a rent raise whose newest and highest month took the credit, the older and
 * lower row would be reported as the tenant's monthly rent.
 */
export function getDisplayMonthlyRentEURFromBookingAndPayments(
  booking: {
    monthly_rent?: number | null;
    rooms?: { price?: number | null } | null;
  } | null,
  options: {
    rentPayments: {
      amount_cents?: number | null;
      gross_amount_cents?: number | null;
      due_date?: string | null;
    }[];
    preferCalendarMonthYm?: string | null;
  }
): number {
  void options.preferCalendarMonthYm;
  const base = getFullMonthlyRentFromBooking(booking);
  const list = options.rentPayments || [];
  if (list.length === 0) return base;

  let payMax = 0;
  for (const p of list) {
    const cents = p.gross_amount_cents ?? p.amount_cents;
    if (cents == null) continue;
    const euro = Number(cents) / 100;
    if (Number.isFinite(euro) && euro > payMax) payMax = euro;
  }
  if (payMax <= 0) return base;
  return roundRentEUR(payMax);
}

/**
 * Expected security deposit. The stored `deposit_amount` (agreed at contract
 * signing) is the source of truth; the room's base deposit, then 2× resolved
 * rent, are fallbacks for bookings where no deposit was ever recorded. The
 * deposit is contractually fixed — a later rent change must not raise the
 * expected deposit, or a tenant who paid in full is shown as "Partial" forever.
 *
 * A tenant-specific deposit discount IS part of what was agreed, so it is
 * subtracted here rather than bolted on at the call sites: this is the figure
 * deposit state, settlement and the SQL cascade all compare paid rows against
 * (see resolve_expected_deposit_eur, COL-360).
 */
export function getExpectedDepositEUR(
  booking:
    | ({
        monthly_rent?: number | null;
        deposit_amount?: number | null;
        rooms?: {
          price?: number | null;
          deposit_amount?: number | null;
        } | null;
      } & DiscountBookingFields)
    | null
): number {
  return effectiveDepositEUR(booking);
}

export type DepositStatus = "paid" | "partial" | "due";

export interface DepositStateResult {
  /** paid | partial | due — the single status every view must render. */
  status: DepositStatus;
  /**
   * Expected deposit (cents): the contractual `bookings.deposit_amount` resolved
   * via getExpectedDepositEUR (stored amount first, 2× rent only as a fallback).
   * This is fixed at contract signing and never drifts with later rent edits — so
   * it, not the sum of billed rows, is what "expected" means.
   */
  expectedCents: number;
  /** Total effectively-paid deposit (cents). */
  paidCents: number;
  /** Headline amount to display (cents) — the contractual deposit (expectedCents). */
  displayCents: number;
  /** Whether any deposit payment row exists for the booking. */
  hasDepositRow: boolean;
  /** Most recent paid_at across effectively-paid rows (ISO), or null. */
  paidAtIso: string | null;
}

/**
 * Resolve the authoritative deposit amount + status for a booking — the single
 * source of truth shared by the admin card, admin table, and the tenant dashboard
 * so all three always agree.
 *
 * Expected comes from getExpectedDepositEUR (the contractual `deposit_amount` fixed
 * at signing), NOT from the sum of billed rows. Row-based "expected" is self-fulfilling
 * under the one-live-deposit-row unique index: a row mis-billed at €500 against a €675
 * contract would show "Paid" and hide the €175 shortfall, and a row billed at 1.5×
 * raised-rent (the very drift this fights) would flip a fully-paid €675 tenant back to
 * "Partial". The stored amount does neither — it is what the tenant agreed to pay and
 * never moves when rent is edited afterwards (see #767 / getExpectedDepositEUR).
 *
 *   expected = getExpectedDepositEUR(booking)   [contractual, drift-free]
 *   paid     = SUM(amount_cents of effectively-paid rows)   [caller supplies the predicate:
 *              Stripe → trust DB 'paid'; manual/bank transfer → requires an approved/present proof]
 *   status   = paid   when expected>0 AND paid>=expected
 *              partial when paid>0 but < expected
 *              due     otherwise
 *
 * Payment rows are used ONLY for paid amount + paid_at, and to report whether a deposit
 * row exists yet. This still models genuine installment deposits (paid accrues across
 * rows until it reaches the contractual expected).
 */
export function resolveDepositState(input: {
  /** All deposit-type payment rows for THIS booking (paid + unpaid). */
  depositPayments: Array<{
    amount_cents?: number | null;
    paid_at?: string | null;
    status?: string | null;
  }>;
  /** Is this deposit row effectively paid? (Stripe: trust DB; manual: needs proof) */
  isEffectivelyPaid: (payment: {
    amount_cents?: number | null;
    paid_at?: string | null;
    status?: string | null;
  }) => boolean;
  /** Contractual expected deposit in EUR (getExpectedDepositEUR) — the source of truth. */
  expectedDepositEUR: number;
}): DepositStateResult {
  // Canceled/failed rows are voided attempts (also how the duplicate-dedup migration
  // retires losers) — they must never count toward paid.
  const rows = (input.depositPayments || []).filter((p) => {
    const s = String(p?.status ?? "").toLowerCase();
    return s !== "canceled" && s !== "failed";
  });
  const paidRows = rows.filter((p) => input.isEffectivelyPaid(p));
  const paidCents = paidRows.reduce(
    (sum, p) => sum + (Number(p?.amount_cents) || 0),
    0
  );
  // Expected is the contractual deposit (fixed at signing) — never the billed rows.
  const expectedCents = Math.max(
    0,
    Math.round((Number(input.expectedDepositEUR) || 0) * 100)
  );

  let status: DepositStatus;
  if (expectedCents > 0 && paidCents >= expectedCents) status = "paid";
  else if (paidCents > 0) status = "partial";
  else status = "due";

  const paidDates = paidRows
    .map((p) => (p?.paid_at ? String(p.paid_at) : ""))
    .filter(Boolean)
    .sort();
  const paidAtIso = paidDates.length ? paidDates[paidDates.length - 1] : null;

  return {
    status,
    expectedCents,
    paidCents,
    displayCents: expectedCents,
    hasDepositRow: rows.length > 0,
    paidAtIso,
  };
}

/**
 * The amount a deposit ROW must be raised to before an admin "the deposit is
 * settled" action can actually settle it — or null when no top-up is warranted.
 *
 * Why this exists: `resolveDepositState` deliberately takes `expected` from the
 * contractual `bookings.deposit_amount` and `paid` from the SUM OF ROW AMOUNTS.
 * The two are written separately, so a row can sit below the contract — most
 * often at €0, minted by a deposit-proof upload while `deposit_amount` was still
 * unset, and never re-amounted when the amount was filled in afterwards. Every
 * admin action then became unsatisfiable: marking the deposit paid attested a €0
 * row, `paid` stayed under `expected`, the journey's deposit gate stayed shut,
 * and a forced stage snapped back on the next refresh — with nothing left in the
 * UI to click.
 *
 * Two guards keep this from overstating what arrived:
 *   - exactly ONE live row. Several live rows are an installment plan, where a
 *     shortfall is deliberate and topping one row up would invent money.
 *   - the row is genuinely BELOW the contractual amount. A row at or above it
 *     needs nothing, and a row above it is never trimmed here — the caller is
 *     asserting settlement, not renegotiating the deposit.
 *
 * A third guard: never touch a row carrying a Stripe identity
 * (`stripe_payment_intent_id` / `stripe_charge_id`). That row is a record of
 * what Stripe was actually told to collect — rewriting `amount_cents` locally
 * would desync the ledger from the real charge, which nothing here can also
 * update. An in-flight SEPA/card deposit collection is exactly this shape: a
 * single live row, "pending", with a Stripe id already stamped.
 *
 * Callers pass rows already filtered to live ones (canceled/failed excluded, the
 * same rule as `resolveDepositState`).
 */
export function depositTopUpCents(input: {
  /** Live deposit rows for the booking (canceled/failed already removed). */
  liveDepositRows: Array<{
    amount_cents?: number | null;
    stripe_payment_intent_id?: string | null;
    stripe_charge_id?: string | null;
  }>;
  /** Contractual deposit in cents (resolveDepositState().expectedCents). */
  expectedCents: number;
}): number | null {
  const rows = input.liveDepositRows || [];
  if (rows.length !== 1) return null;
  const [row] = rows;
  if (row?.stripe_payment_intent_id || row?.stripe_charge_id) return null;
  const expected = Number(input.expectedCents) || 0;
  if (expected <= 0) return null;
  const have = Number(row?.amount_cents) || 0;
  return have < expected ? expected : null;
}

/**
 * Is a single payment row EFFECTIVELY paid?
 *
 * Stripe rows are authoritative — the webhook flips them and there is nothing
 * to corroborate. Manual / bank-transfer rows need CORROBORATION for the same
 * reason the journey gates need artefacts: `status = 'paid'` on its own is
 * somebody's word for it, and nothing records whose.
 *
 * Two things corroborate a manual row:
 *   1. a transfer proof — an approved proof counts even if nobody remembered to
 *      flip the row, and a present (pending) proof backs an already-flipped one;
 *   2. an ADMIN ATTESTATION — `marked_paid_at` / `marked_paid_by`, written by
 *      the admin "Mark paid" actions (20260802110000_journey_evidence_followups).
 *      Cash and off-platform transfers are legitimate and often have no
 *      uploadable proof; what makes them evidence rather than a claim is that
 *      the row records WHO settled it and WHEN. An unattributed `paid` with
 *      neither (legacy rows, imports, a status flipped by another surface) still
 *      does not count.
 *
 * Extracted from AdminTenants so the tenant list, the tenant profile and the
 * journey gate can no longer disagree about whether a deposit is settled.
 */
export function isPaymentEffectivelyPaid(
  payment: {
    id?: string | number | null;
    status?: string | null;
    stripe_payment_intent_id?: string | null;
    stripe_charge_id?: string | null;
    marked_paid_at?: string | null;
    marked_paid_by?: string | null;
    amount_cents?: number | null;
    received_cents?: number | null;
  } | null,
  proofFor: (paymentId: string) => { status?: string | null } | null | undefined
): boolean {
  if (!payment) return false;
  const st = String(payment.status ?? "").toLowerCase();
  if (payment.stripe_payment_intent_id || payment.stripe_charge_id)
    return st === "paid";
  // COL-417: short of what was billed is never settled, whatever is attached.
  // Must stay ABOVE the proof check — see isShortOfExpected.
  if (isShortOfExpected(payment)) return false;
  const proof = proofFor(String(payment.id ?? ""));
  if (String(proof?.status ?? "").toLowerCase() === "approved") return true;
  if (st !== "paid") return false;
  return !!proof || isAdminAttestedPaid(payment);
}

/**
 * Has money arrived against this row, but less than the full billed amount?
 *
 * The exact mirror of the gate in `recompute_booking_rent_coverage`
 * (20260915150100), and it has to sit in the same PLACE in the chain: above the
 * approved-proof branch, below the Stripe branch.
 *
 * WHY THE POSITION MATTERS. The proof branch short-circuits to `true` before
 * anything looks at an amount, and `confirm_reconciliation_match` used to close
 * a month precisely by attaching an approved proof. Below the proof branch this
 * gate would never run, and a reconciled EUR 120 transfer against a EUR 650
 * month would read as settled — stopping the tenant's reminders and resetting
 * their accrued Mahngebuehren, the two things COL-417 says must not change.
 *
 * Two conditions on purpose. `partially_paid` is the status the receipts trigger
 * maintains. The received/billed comparison is the backstop for a row some other
 * surface left with money against it under a different status — "is this paid"
 * being answered in several places is the recurring bug this guards against.
 *
 * Deliberately silent when `received_cents` was not selected (undefined) or is
 * zero: every query written before COL-417 omits the column, and a row that has
 * received nothing is judged by the corroboration rules exactly as before. The
 * gate only ever fires on evidence of a genuine part-payment, so it can never
 * demote a row that was settled before this feature existed.
 */
function isShortOfExpected(payment: {
  status?: string | null;
  amount_cents?: number | null;
  received_cents?: number | null;
}): boolean {
  if (String(payment.status ?? "").toLowerCase() === "partially_paid")
    return true;
  const received = Number(payment.received_cents ?? 0);
  const expected = Number(payment.amount_cents ?? 0);
  if (!Number.isFinite(received) || !Number.isFinite(expected)) return false;
  return received > 0 && received < expected;
}

/**
 * What is actually still owed on a rent row, in cents — `amount_cents` minus
 * whatever the receipts ledger has already recorded against it.
 *
 * COL-417 review fix: card checkout (`create-card-checkout` and its
 * `Dashboard.tsx` callsites) charged the full `amount_cents` even once a
 * `partially_paid` row had money against it, so a tenant with EUR 200 of
 * EUR 650 already received was charged EUR 650 again instead of the EUR 450
 * still open. Every "how much do I charge this row" callsite should go through
 * this function rather than reading `amount_cents` directly.
 *
 * Note the deliberate asymmetry with `resolveRentSettlement`: this takes the
 * row at its word, with no proof/attestation gate, because it answers "what
 * would I charge a card right now", where over-charging is the harm. The
 * settlement resolver answers "what may we DISPLAY as received", where an
 * uncorroborated claim is the harm.
 */
export function remainingRentCents(payment: {
  amount_cents?: number | null;
  received_cents?: number | null;
}): number {
  const amount = Number(payment.amount_cents ?? 0);
  const received = Number(payment.received_cents ?? 0);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (!Number.isFinite(received) || received <= 0) return amount;
  return Math.max(0, amount - received);
}

/**
 * Did an admin explicitly attest this row as settled? Either half of the pair
 * is enough — `marked_paid_at` is always written, `marked_paid_by` is null when
 * the acting user can't be resolved. Module-local: the rule belongs to
 * `isPaymentEffectivelyPaid` and is covered through it.
 */
function isAdminAttestedPaid(
  payment: {
    marked_paid_at?: string | null;
    marked_paid_by?: string | null;
  } | null
): boolean {
  if (!payment) return false;
  return (
    (payment.marked_paid_at != null && String(payment.marked_paid_at) !== "") ||
    (payment.marked_paid_by != null && String(payment.marked_paid_by) !== "")
  );
}

/**
 * The latest proof row per payment id, from rows already ordered
 * `created_at desc` (every caller queries `payment_proofs` that way) — first
 * seen per id wins. Shared so the "latest proof" rule behind
 * `isPaymentEffectivelyPaid` can't drift between the screens that back it
 * with their own `payment_proofs` query.
 */
export function latestProofByPaymentId<T extends { payment_id: string }>(
  proofRows: T[]
): Map<string, T> {
  const map = new Map<string, T>();
  for (const row of proofRows) {
    if (!map.has(row.payment_id)) map.set(row.payment_id, row);
  }
  return map;
}

/**
 * The status a payment row should DISPLAY — one step removed from the raw
 * `payments.status` column.
 *
 *  - "paid"       corroborated: Stripe, a transfer proof, or an admin attestation.
 *  - "unverified" the column says paid but nothing backs it: no proof and no
 *                 record of who marked it. Its own state on purpose. Rendering
 *                 this as "Paid" is what made the Payments tab contradict the
 *                 deposit gate and every rollup, which all use
 *                 `isPaymentEffectivelyPaid` — an admin saw green and trusted
 *                 money that was never confirmed received.
 *  - "sepa_in_progress" a direct debit was submitted to the bank and has not
 *                 settled yet (2–5 business days). Neither open nor paid: the
 *                 tenant has nothing left to do, and nobody must re-collect it.
 *  - "overdue"    the row's own escalated state (overdue / inkasso).
 *  - "failed"     a collection that BOUNCED (e.g. a returned SEPA debit). Still
 *                 owed and needs re-collecting, so it must not read "Pending"
 *                 like a month that was simply never charged yet.
 *  - "pending"    everything else still owed.
 *
 * Lives here rather than in the badge component so the rule is testable and so
 * the badge can only choose colours, not semantics.
 */
export type PaymentDisplayStatus =
  | "paid"
  /**
   * COL-417: money has arrived against the row, but less than was billed. Its
   * own state on purpose — "Pending" would tell an admin nothing was received
   * and invite a second collection, "Paid" would hide a real shortfall.
   */
  | "partially_paid"
  /**
   * Settled by CoLiviq rather than by the tenant — currently only the referral
   * free month. Distinct from "paid" on purpose: the row IS closed and must
   * never be charged or chased, but telling a tenant they "paid" a month they
   * were given reads as a mistake in their statement, and an admin looking at
   * an EUR 0 paid row deserves to know why it is zero.
   */
  | "waived"
  | "unverified"
  | "sepa_in_progress"
  | "overdue"
  | "failed"
  | "pending";

/**
 * Was this rent row waived by a referral milestone?
 *
 * Reads the stamp apply_referral_bonus leaves on the payment itself, so the
 * answer travels with the row into every surface that renders it — no join, no
 * second query, and no way for the referrals feature and the payments screen to
 * disagree about which month was free.
 */
export function isReferralFreeMonth(
  payment: { metadata?: unknown } | null | undefined
): boolean {
  const meta = payment?.metadata;
  if (!meta || typeof meta !== "object") return false;
  return !!(meta as Record<string, unknown>).referral_free_month;
}

/**
 * How much of this rent row was paid for by referral credits (EUR 100 each).
 *
 * Reads the `referral_credits` stamp apply_referral_reward leaves on the
 * payment — the same "the fact travels with the row" reasoning as
 * isReferralFreeMonth. `amount_cents` is what the tenant is charged;
 * `amount_cents + this` is the rent the row was for (`gross_amount_cents`).
 * 0 when the row carries no credit.
 */
export function getReferralCreditCents(
  payment: { metadata?: unknown } | null | undefined
): number {
  const meta = payment?.metadata;
  if (!meta || typeof meta !== "object") return 0;
  const credits = (meta as Record<string, unknown>).referral_credits;
  if (!Array.isArray(credits)) return 0;
  let total = 0;
  for (const c of credits) {
    const cents = Number((c as { cents?: unknown })?.cents);
    if (Number.isFinite(cents) && cents > 0) total += Math.round(cents);
  }
  return total;
}

/**
 * SEPA statuses a row can hold while a debit is in flight at the bank.
 * `overdue` is in the list on purpose: dunning escalates an unpaid row by due
 * date and does not know a debit was submitted, so an in-flight row can already
 * carry the escalated status.
 */
const SEPA_IN_FLIGHT_ROW_STATUSES = ["pending", "scheduled", "overdue"];

/**
 * Is a SEPA debit submitted to the bank but not yet settled?
 *
 * A SEPA collection is asynchronous: Stripe confirms the PaymentIntent into
 * `processing` (we store the row as `pending` with the intent id stamped) and
 * the money only lands 2–5 business days later, when the webhook flips the row
 * to paid/failed. Between those two moments the row is neither "open" nor
 * "paid", and showing it as overdue tells the tenant to pay again for money
 * already on its way.
 *
 * A stamped intent id on an unpaid row is what identifies that window. It
 * cannot be confused with a card payment: the card flow (create-card-checkout →
 * confirm-payment-from-session) only ever writes `stripe_payment_intent_id`
 * together with `status = 'paid'`, so an UNPAID row carrying an intent is
 * always an off-session SEPA charge from create-rent-payment / charge-rent-sepa
 * / rent-charge-tick. A debit that bounced is excluded — the webhook writes
 * `failed`, which is money still owed and must not read as "in progress".
 */
export function isSepaDebitInFlight(
  payment: {
    status?: string | null;
    paid_at?: string | null;
    stripe_payment_intent_id?: string | null;
  } | null
): boolean {
  if (!payment) return false;
  if (!payment.stripe_payment_intent_id) return false;
  if (payment.paid_at) return false;
  return SEPA_IN_FLIGHT_ROW_STATUSES.includes(
    String(payment.status ?? "").toLowerCase()
  );
}

export function resolvePaymentDisplayStatus(
  payment: {
    id?: string | number | null;
    status?: string | null;
    paid_at?: string | null;
    stripe_payment_intent_id?: string | null;
    stripe_charge_id?: string | null;
    marked_paid_at?: string | null;
    marked_paid_by?: string | null;
    amount_cents?: number | null;
    received_cents?: number | null;
    metadata?: unknown;
  } | null,
  proofFor: (paymentId: string) => { status?: string | null } | null | undefined
): PaymentDisplayStatus {
  // Before the paid check: a waived month is also "paid" by every mechanical
  // test, and would otherwise disappear into the generic paid bucket.
  if (isReferralFreeMonth(payment)) return "waived";
  if (isPaymentEffectivelyPaid(payment, proofFor)) return "paid";
  const raw = String(payment?.status ?? "").toLowerCase();
  if (raw === "paid") return "unverified";
  if (raw === "failed") return "failed";
  // After "failed" (a bounced collection is the more urgent fact) but before
  // sepa/overdue/pending: a part-paid month is still chased and still accrues
  // fees, and the row renders its own overdue reminder line independently of
  // this chip, so surfacing the shortfall here loses nothing and stops an
  // admin reading "Pending" as "nothing arrived".
  if (payment && isShortOfExpected(payment)) return "partially_paid";
  // Before "overdue" on purpose: a debit submitted on the due date is still in
  // flight when dunning escalates the row, and "Overdue" would tell the tenant
  // (and the admin) to collect money that is already on its way.
  if (isSepaDebitInFlight(payment)) return "sepa_in_progress";
  if (/overdue|inkasso/.test(raw)) return "overdue";
  return "pending";
}

/**
 * The one answer to "where does this month stand?" — expected, received,
 * remaining, and the state that follows from them (COL-417).
 *
 * Every surface that shows a partial payment reads this rather than doing its
 * own subtraction. The arithmetic is trivial; having it in one place is not,
 * because `expected` and `received` are written by different mechanisms
 * (`amount_cents` by the rent materializer / admin edits, `received_cents` by
 * the receipts trigger) and a screen that mixes up which one it is holding
 * shows the tenant a number nobody owes.
 *
 * BACK-COMPATIBILITY. A row settled before COL-417 existed has no receipts, and
 * most queries in this repo still do not select `received_cents` at all. Such a
 * row reports `received = expected` when it is effectively paid, so every
 * existing "how much came in" question keeps its old answer and no historical
 * month reads as suddenly unpaid. The migration backfills the column for the
 * same reason on the SQL side; this handles the callers that never select it.
 */
export function resolveRentSettlement(
  payment: {
    id?: string | number | null;
    status?: string | null;
    stripe_payment_intent_id?: string | null;
    stripe_charge_id?: string | null;
    marked_paid_at?: string | null;
    marked_paid_by?: string | null;
    amount_cents?: number | null;
    received_cents?: number | null;
  } | null,
  proofFor: (paymentId: string) => { status?: string | null } | null | undefined
): {
  expectedCents: number;
  receivedCents: number;
  /** Never negative — an overpayment is surfaced separately, not as a negative balance. */
  remainingCents: number;
  /**
   * `receivedCents` capped at what this month was billed — how much of THIS
   * month's bill the money on this row actually covers.
   *
   * This, not `receivedCents`, is what a rollup across rows must sum. An
   * overpayment lives twice on purpose: `carry_forward_overpayment()` leaves
   * the full amount on the overpaid row's `received_cents` AND inserts a
   * `credit_carryover` receipt on the next open month, which raises that row's
   * `received_cents` too. Summing the raw figure counts those euros on both
   * rows; summing this one counts them once, on the month they settle.
   */
  appliedCents: number;
  /** Cents received beyond what was billed; the carry-forward credit. */
  overpaidCents: number;
  isSettled: boolean;
  isPartial: boolean;
} {
  const expectedCents = Math.max(
    0,
    Math.round(Number(payment?.amount_cents ?? 0))
  );
  const settled = isPaymentEffectivelyPaid(payment, proofFor);
  const rawReceived = Number(payment?.received_cents ?? 0);
  const receivedFromLedger =
    Number.isFinite(rawReceived) && rawReceived > 0
      ? Math.round(rawReceived)
      : 0;
  // COL-417 review fix: a settled row is settled for the WHOLE month, however
  // stale `received_cents` is — the column is not a complete record of what
  // arrived. Neither Stripe path writes it (`stripe-webhook` never references
  // the column; `confirm-payment-from-session` sets status:'paid' without it)
  // and `mark_payment_paid_from_proof` leaves it untouched, so a EUR 650 month
  // part-paid EUR 200 by transfer and finished on card keeps `received_cents`
  // at 20000 while the row is authoritatively paid.
  //
  // Taking the ledger figure at face value there returned isSettled AND
  // isPartial at once: a "Paid" chip next to a "EUR 200.00 of EUR 650.00" bar,
  // and — because `appliedCents` fed the paid rollup while `unpaid()` dropped
  // the row from outstanding — EUR 450 missing from both tiles, breaking the
  // invariant that the two sum to what was billed. Floor a settled row at its
  // billed amount so every derived figure below stays honest; the pre-existing
  // zero case is the same rule, just the part of it that was already written.
  const receivedCents = settled
    ? Math.max(receivedFromLedger, expectedCents)
    : receivedFromLedger;
  const remainingCents = Math.max(0, expectedCents - receivedCents);
  return {
    expectedCents,
    receivedCents,
    remainingCents,
    appliedCents: Math.min(receivedCents, expectedCents),
    overpaidCents: Math.max(0, receivedCents - expectedCents),
    isSettled: settled,
    isPartial: receivedCents > 0 && receivedCents < expectedCents,
  };
}

/**
 * The statuses that mean "this rent month is still owed".
 *
 * This list existed, unnamed, in at least eight places — the tenant dashboard
 * (twice), the admin tenant list, the SEPA-charge gate, the reconciliation
 * queue, the finAPI candidate query, the rent materializer, and the Stripe
 * lookups. Adding `partially_paid` to the union type is checked by the
 * compiler; adding it to a hand-written `["pending","scheduled","overdue"]` is
 * not, so every one of those was a silent bug the day partial payments shipped:
 * a part-paid month vanished from "what is owed" while still being owed.
 *
 * Use this instead of writing the array again. If a call site needs a DIFFERENT
 * set (the materializer also treats `paid` as blocking, because it asks "does a
 * live row exist" rather than "is money owed"), say so explicitly there rather
 * than quietly diverging from this one.
 */
export const OPEN_RENT_STATUSES = [
  "pending",
  "scheduled",
  "partially_paid",
  "overdue",
] as const;

/** Is this rent row still owed, by status alone? See OPEN_RENT_STATUSES. */
export function isOpenRentStatus(status?: string | null): boolean {
  return (OPEN_RENT_STATUSES as readonly string[]).includes(
    String(status ?? "")
  );
}

/**
 * The deposit signal consumed by the tenant-journey gate: is the FULL
 * contractual deposit settled?
 *
 * Deliberately routed through `resolveDepositState` rather than
 * `payments.some(p => p.status === "paid")` — a part-payment, or a manual row
 * flipped to "paid" with nothing behind it (no proof, no admin attestation),
 * must not let a tenant climb past "Deposit pending".
 */
export function isDepositFullyPaid(input: {
  booking: {
    monthly_rent?: number | null;
    deposit_amount?: number | null;
    rooms?: { price?: number | null } | null;
  } | null;
  /** All payment rows for the booking (any type — deposits are filtered here). */
  payments: Array<{
    id?: string | number | null;
    type?: string | null;
    amount_cents?: number | null;
    paid_at?: string | null;
    status?: string | null;
    stripe_payment_intent_id?: string | null;
    stripe_charge_id?: string | null;
    marked_paid_at?: string | null;
    marked_paid_by?: string | null;
  }>;
  /** Latest proof per payment id, for the manual-payment rule above. */
  proofFor: (
    paymentId: string
  ) => { status?: string | null } | null | undefined;
}): boolean {
  const depositPayments = (input.payments || []).filter(
    (p) => p?.type === "deposit"
  );
  const expectedDepositEUR = getExpectedDepositEUR(input.booking);
  // Nothing is owed → the gate is vacuously satisfied. `resolveDepositState`
  // reports "due" for a zero expectation (it can't tell "waived" from "not
  // configured yet"), which would otherwise strand such a tenancy at Deposit
  // pending forever, with no deposit to collect that could ever release it.
  if (expectedDepositEUR <= 0) return true;
  return (
    resolveDepositState({
      depositPayments,
      isEffectivelyPaid: (p) =>
        isPaymentEffectivelyPaid(p as any, input.proofFor),
      expectedDepositEUR,
    }).status === "paid"
  );
}

/** First calendar month charge (pro-rata or override), independent of "today". */
export function computeFirstMonthRentEUR(
  fullMonthlyRent: number,
  moveInIso: string | null | undefined,
  firstMonthOverride: number | null | undefined
): number {
  if (fullMonthlyRent <= 0) return 0;
  if (
    firstMonthOverride != null &&
    Number.isFinite(Number(firstMonthOverride))
  ) {
    return roundRentEUR(Number(firstMonthOverride));
  }
  const mi = (moveInIso || "").trim().slice(0, 10);
  if (!mi) return roundRentEUR(fullMonthlyRent);
  return roundRentEUR(fullMonthlyRent * getMoveInProRataFraction(mi));
}

/**
 * Amount to show and bill for the current period: pro-rata in the move-in calendar month, else full monthly rent.
 */
export function getDisplayedRentEUR(params: {
  fullMonthlyRent: number;
  moveInIso: string | null | undefined;
  firstMonthOverride: number | null | undefined;
  asOfIsoDate: string;
}): {
  amount: number;
  fullMonthlyRent: number;
  isPartialFirstMonth: boolean;
} {
  const { fullMonthlyRent, moveInIso, firstMonthOverride, asOfIsoDate } =
    params;
  const moveIn = (moveInIso || "").trim().slice(0, 10);
  const asOf = (asOfIsoDate || "").trim().slice(0, 10);

  if (fullMonthlyRent <= 0) {
    return { amount: 0, fullMonthlyRent: 0, isPartialFirstMonth: false };
  }

  if (!moveIn) {
    return {
      amount: roundRentEUR(fullMonthlyRent),
      fullMonthlyRent: roundRentEUR(fullMonthlyRent),
      isPartialFirstMonth: false,
    };
  }

  // Before move-in day, or still in the move-in calendar month: show first-month (pro-rata) charge
  const beforeMoveInDay = asOf < moveIn;
  const inMoveInMonth = isoSameYearMonth(moveIn, asOf);

  if (beforeMoveInDay || inMoveInMonth) {
    const amount = computeFirstMonthRentEUR(
      fullMonthlyRent,
      moveIn,
      firstMonthOverride
    );
    const isPartialFirstMonth =
      roundRentEUR(amount) < roundRentEUR(fullMonthlyRent);
    return {
      amount,
      fullMonthlyRent: roundRentEUR(fullMonthlyRent),
      isPartialFirstMonth,
    };
  }

  return {
    amount: roundRentEUR(fullMonthlyRent),
    fullMonthlyRent: roundRentEUR(fullMonthlyRent),
    isPartialFirstMonth: false,
  };
}

/**
 * Expected rent (cents) for a payment row's due calendar month — same rules as the admin rent grid / `ensureRentPaymentForMonth`.
 * Uses the 15th of that month as `asOfIsoDate` for first-month vs full-month logic.
 */
export function getExpectedRentCentsForDueCalendarMonth(params: {
  fullMonthlyRent: number;
  moveInIso: string | null | undefined;
  firstMonthOverride: number | null | undefined;
  dueYear: number;
  dueMonthIndex0: number;
}): number {
  const {
    fullMonthlyRent,
    moveInIso,
    firstMonthOverride,
    dueYear,
    dueMonthIndex0,
  } = params;
  const monthMid = `${dueYear}-${String(dueMonthIndex0 + 1).padStart(2, "0")}-15`;
  const { amount } = getDisplayedRentEUR({
    fullMonthlyRent,
    moveInIso,
    firstMonthOverride,
    asOfIsoDate: monthMid,
  });
  return Math.round(roundRentEUR(amount) * 100);
}

/**
 * Clamp a target day-of-month (1-31) into a specific `YYYY-MM` month, e.g. day
 * 31 in February becomes the 28th (or 29th). Shared by the admin rent-due-day
 * override (COL-410) and the rent-schedule projection, which both need to
 * pick a real calendar date for an arbitrary chosen day.
 */
export function clampDueDayInMonth(day: number, ym: string): number {
  const year = Number(ym.slice(0, 4));
  const monthIndex0 = Number(ym.slice(5, 7)) - 1;
  const daysInMonth = new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
  return Math.min(Math.max(1, Math.round(day)), daysInMonth);
}

/** "1st" / "2nd" / "3rd" / "4th" ... — used in rent-due-day admin copy. */
export function ordinalLabel(day: number): string {
  const n = Math.round(day);
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** Split rent into base vs utilities line items, scaled to a billing total (e.g. pro-rata first month). */
export function splitRentLineItems(
  fullMonthlyRent: number,
  billingTotal: number
): { base: number; utilities: number } {
  if (fullMonthlyRent <= 0 || billingTotal <= 0)
    return { base: 0, utilities: 0 };
  const util = 100;
  if (fullMonthlyRent <= util) {
    return { base: 0, utilities: roundRentEUR(billingTotal) };
  }
  const basePart = fullMonthlyRent - util;
  const scale = billingTotal / fullMonthlyRent;
  return {
    base: roundRentEUR(basePart * scale),
    utilities: roundRentEUR(util * scale),
  };
}

/** How many months ahead the rent schedule looks past the current one. */
export const RENT_SCHEDULE_MONTHS_AHEAD = 2;

/**
 * Last month the rent schedule covers, as a `YYYY-MM` bound: the current month
 * plus RENT_SCHEDULE_MONTHS_AHEAD.
 */
export function rentScheduleWindowEnd(now: Date = new Date()): string {
  const d = new Date(
    now.getFullYear(),
    now.getMonth() + RENT_SCHEDULE_MONTHS_AHEAD,
    1
  );
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Split rent rows into the two cards on the tenant Payments tab.
 *
 *   history  = PAID rent only.
 *   schedule = everything still owed, through the two-month window.
 *
 * The dividing line is PAYMENT, not the calendar: the current month stays in the
 * schedule until it is marked paid and only then moves to history. An overdue
 * back-month also stays in the schedule — money owed must remain in the
 * actionable card instead of being filed away as history.
 *
 * `isPaid` is the caller's corroborated-paid rule (isPaymentEffectivelyPaid), so
 * a row claiming "paid" with nothing behind it stays in the schedule.
 *
 * `laterOpen` catches rows still owed but dated beyond the window, so no unpaid
 * rent can disappear between the two cards — the caller must surface the count
 * even though the rows themselves aren't listed.
 */
export function splitRentRows<T extends { due_date?: string | null }>(
  rows: readonly T[],
  isPaid: (row: T) => boolean,
  /**
   * VOIDED rows — owed by nobody, listed nowhere. Pass canceled only: a
   * `failed` collection (a bounced SEPA debit) is still money owed and has to
   * stay in the schedule where someone can act on it. Canceled is what the
   * duplicate-dedup migration writes to retire a superseded row.
   */
  isVoid: (row: T) => boolean,
  now: Date = new Date()
): { history: T[]; schedule: T[]; laterOpen: T[] } {
  const windowEnd = rentScheduleWindowEnd(now);
  const byDueDate = (a: T, b: T) =>
    String(a.due_date ?? "").localeCompare(String(b.due_date ?? ""));
  // History reads newest-paid-first (matches splitOwnerInvoices) — an admin
  // opening the card wants the latest settled month, not one from years ago.
  const byDueDateDesc = (a: T, b: T) => byDueDate(b, a);
  const open = rows.filter((r) => !isPaid(r) && !isVoid(r));
  const inWindow = (r: T) => String(r.due_date ?? "").slice(0, 7) <= windowEnd;
  return {
    history: rows.filter((r) => isPaid(r)).sort(byDueDateDesc),
    schedule: open.filter(inWindow).sort(byDueDate),
    laterOpen: open.filter((r) => !inWindow(r)).sort(byDueDate),
  };
}

/**
 * The single canonical "next payment" row for a tenancy: the earliest unpaid
 * rent row, any date — overdue included, never skipped in favor of a later
 * row. This is what admin's tenant-profile `nextDue` already does; every
 * consumer of "next payment" on the tenant side (the summary tile, the
 * amount charged, the due date shown, and what Pay Now / bank transfer
 * actually settles) must resolve through this one function so they can never
 * disagree with each other or with admin.
 *
 * `isVoid` should be canceled-only, matching `splitRentRows` above: a
 * `failed` collection is still money owed and must stay eligible.
 *
 * A row already covered by an effectively-paid row for the same due-month is
 * skipped (e.g. a Stripe-paid row plus a stale duplicate pending row for the
 * same month) so a settled month never resurfaces as "next payment".
 */
export function selectEarliestUnpaidRentPayment<
  T extends { due_date?: string | null; id?: string | number | null },
>(
  rows: readonly T[],
  isPaid: (row: T) => boolean,
  isVoid: (row: T) => boolean
): T | null {
  const monthOf = (r: T) => String(r.due_date ?? "").slice(0, 7);
  const paidMonthKeys = new Set(
    rows.filter((r) => r.due_date && isPaid(r)).map(monthOf)
  );
  const candidates = rows.filter(
    (r) =>
      r.due_date != null &&
      !isPaid(r) &&
      !isVoid(r) &&
      !paidMonthKeys.has(monthOf(r))
  );
  if (candidates.length === 0) return null;
  candidates.sort((a, b) =>
    String(a.due_date ?? "").localeCompare(String(b.due_date ?? ""))
  );
  return candidates[0];
}

/**
 * Whether a `lastPaidRent` row (the most recently due paid rent row) actually
 * covers the period about to be displayed as "upcoming" (`nextPaymentYm`,
 * derived from the due date `selectEarliestUnpaidRentPayment` falls back to
 * when no real row exists yet for the next cycle). If it doesn't — the
 * displayed period has already rolled forward past that paid row's month —
 * the paid row belongs to a past cycle and must not be shown as if it settled
 * the upcoming one.
 */
export function lastPaidRentCoversPeriod(
  lastPaidRentDueDate: string | null | undefined,
  nextPaymentDueDate: string | null | undefined
): boolean {
  return isoSameYearMonth(lastPaidRentDueDate, nextPaymentDueDate);
}

/**
 * Amount to display for the upcoming rent charge when no real payments row
 * exists yet for the next cycle. Computes the expected amount for the actual
 * due month (`nextPaymentDate`) via `getExpectedRentCentsForDueCalendarMonth`
 * — the same pro-rata rules the admin grid and `ensureRentPaymentForMonth`
 * use — rather than a flat "current period estimate vs. full rent" guess,
 * so a projected future first-billed (pro-rata) month still displays its
 * true pro-rata amount instead of the full contractual rent.
 */
export function projectedUpcomingChargeEUR(params: {
  nextPaymentDate: Date;
  fullMonthlyRent: number;
  moveInIso: string | null | undefined;
  firstMonthOverride: number | null | undefined;
}): number {
  const { nextPaymentDate, fullMonthlyRent, moveInIso, firstMonthOverride } =
    params;
  // UTC accessors, not local: `nextPaymentDate` is commonly built from a
  // `YYYY-MM-DD` due-date string (UTC midnight), and in negative-UTC-offset
  // browser timezones the local calendar day rolls back into the prior
  // month — `getMonth()` would then project the wrong due month. This also
  // keeps the projected month consistent with `lastPaidRentCoversPeriod`'s
  // own UTC-ISO period comparison in the caller.
  return (
    getExpectedRentCentsForDueCalendarMonth({
      fullMonthlyRent,
      moveInIso,
      firstMonthOverride,
      dueYear: nextPaymentDate.getUTCFullYear(),
      dueMonthIndex0: nextPaymentDate.getUTCMonth(),
    }) / 100
  );
}
